import { Component, type ErrorInfo, type ReactNode } from "react";
import { reportClientFault } from "../game/client-report";
import { clientTag, translatedTag } from "../game/crash-context";
import { clearAppCrash, markAppCrash, type TableReturn } from "../game/crash-return";
import { fa } from "../i18n/fa";

/**
 * The last thing between a render throw and a blank screen.
 *
 * There was no error boundary anywhere in this app. React 18 rethrows an uncaught render error to
 * `window.onerror`, so `installGlobalErrorRouting` did at least SEE it — but only after React had
 * already unmounted the entire tree, leaving the user a blank page with no way out, and the report
 * carried no idea of WHERE in the UI it happened.
 *
 * This changes both halves: the fault is reported WITH the component stack, and the user gets a
 * real screen with a way to recover instead of nothing.
 *
 * ⚠️ IT REPORTS, IT DOES NOT SWALLOW. A boundary that quietly renders a fallback is how a bug
 * lives for months — the whole reason this app has a beacon at all is that a screen-photo was
 * once the only evidence available.
 *
 * ⚠️ AND A CRASH IS A LOST CONNECTION, NOT A «خروج» (card-a5 item 3). Showing the fallback unmounts
 * the online table under it, and that table's cleanup closes its socket the way the crash latch says
 * (`game/crash-return.ts`): raised in `componentDidCatch`, the layout phase of the commit that shows the
 * fallback, before that cleanup runs — so the server holds the seat for its drop grace instead of
 * handing it to a bot at once. The fallback itself closes, vacates and charges nothing; it offers the
 * way back to the table (`tableReturn`).
 */
interface Props {
  children: ReactNode;
  /**
   * The crash screen's door back to the live table — `crashReturn(store)` from `main.tsx`, asked each
   * time the fallback renders; null (or absent) when this device holds no table to go back to, and
   * then the screen is exactly what it always was: one button, the reload.
   */
  tableReturn?: (() => TableReturn | null) | undefined;
}
interface State {
  failed: boolean;
}

export class ErrorBoundary extends Component<Props, State> {
  override state: State = { failed: false };

  static getDerivedStateFromError(): State {
    // NOTHING ELSE HERE: this runs in the render phase, in renders React may still throw away (it
    // retries a failed render once, and commits the children when the retry succeeds). The crash
    // latch is raised at commit, below.
    return { failed: true };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // THE CRASH THIS COMMIT IS SHOWING — the layout phase, before the passive phase in which the
    // crashed subtree's effects clean up (`OnlineGame` closes its table there, reading this latch).
    markAppCrash();
    // The component stack is the piece the window-level handler cannot give us: it names the
    // subtree that threw, which is the difference between "the app crashed" and a place to look.
    reportClientFault("render-crash", {
      error,
      // Cap it — the sink clamps to 900 chars and the top frames are the informative ones.
      stack: (info.componentStack ?? "").trim().split("\n").slice(0, 6).join(" ← ").slice(0, 400),
      // ⚠️ AFTER `stack`, AND THAT ORDERING IS LOAD-BEARING. The detail is joined in insertion
      // order and `fingerprintOf` hashes the first 200 characters, so a field placed BEFORE the
      // (up to 400-char) stack would split one fault into an ErrorLog row per value. Behind the
      // stack they enrich the report without touching how it is grouped.
      //
      // WHY THESE TWO: the same DOM `NotFoundError` has now hit the same subtree twice, on two
      // engines, and nothing in that subtree mutates the DOM itself — so the question is whether
      // something OUTSIDE React was rewriting it. See `crash-context.ts`.
      engine: clientTag(),
      translated: translatedTag(),
    });
    console.error("[app] render crash", error, info.componentStack);
  }

  /** Back to the table: the crash is over (the latch is lowered FIRST, so the table that mounts
   *  next closes like any other on its way out), the session re-enters the table through its
   *  ticket, and the tree under the boundary renders afresh — no reload, no boot. */
  private readonly returnToTable = (way: TableReturn): void => {
    clearAppCrash();
    way.go();
    this.setState({ failed: false });
  };

  /** The door, if there is one — never allowed to throw from inside the fallback. */
  private wayBack(): TableReturn | null {
    try {
      return this.props.tableReturn?.() ?? null;
    } catch {
      return null;
    }
  }

  override render(): ReactNode {
    if (!this.state.failed) return this.props.children;
    const way = this.wayBack();
    return (
      <div
        role="alert"
        style={{
          position: "fixed",
          inset: 0,
          display: "grid",
          placeItems: "center",
          padding: 24,
          textAlign: "center",
          // Inline, NOT Tailwind classes: a render crash can coincide with styling being the very
          // thing that is broken, and a fallback that needs the stylesheet to be readable is no
          // fallback at all.
          background: "#0f1412",
          color: "#f4f6f5",
          font: "16px/1.7 system-ui, sans-serif",
        }}
      >
        <div style={{ maxWidth: 320 }}>
          <div style={{ fontSize: 44 }}>🎴</div>
          <p style={{ margin: "12px 0 20px", whiteSpace: "pre-line" }}>{fa.status.renderCrash}</p>
          {/* THE WAY BACK TO THE TABLE (card-a5 item 3) — the maintenance screen's door, in its
              words: this device's ticket, re-joined. Only when there is a table to go back to; the
              reload below stays, as the second way out, for a crash the table cannot get past. */}
          {way ? (
            <button
              type="button"
              onClick={() => this.returnToTable(way)}
              style={{ ...BUTTON, ...PRIMARY, display: "block", margin: "0 auto 12px" }}
            >
              {way.pending ? fa.maintenance.returnToTable : fa.maintenance.returnToGame}
            </button>
          ) : null}
          <button
            type="button"
            onClick={() => window.location.reload()}
            style={{ ...BUTTON, ...(way ? SECONDARY : PRIMARY) }}
          >
            {fa.status.retry}
          </button>
        </div>
      </div>
    );
  }
}

// Inline, like the rest of the fallback: the stylesheet may be the very thing that broke.
const BUTTON = {
  appearance: "none",
  borderRadius: 12,
  padding: "12px 28px",
  fontWeight: 700,
  fontSize: 16,
  cursor: "pointer",
} as const;
/** The one action the screen leads with — the colours the lone reload has always had. */
const PRIMARY = { border: 0, background: "#e9c349", color: "#1c211e" } as const;
/** The reload, once there is a way back to lead with instead. */
const SECONDARY = {
  border: "1px solid rgba(244, 246, 245, 0.35)",
  background: "transparent",
  color: "#f4f6f5",
} as const;
